import { useEffect, useState } from 'react';
import { api } from '../api/client';

/**
 * Turn browser notifications on for this device (ADR-0020).
 *
 * Two browser rules shape everything here:
 *
 *  - The permission prompt MUST come from a user gesture, and a denial is STICKY — the browser will
 *    not ask again, and no amount of clicking will re-open it. So it is never triggered on load, and
 *    a denial is explained rather than retried.
 *  - On iOS this only works once the console has been added to the home screen. Saying so plainly is
 *    better than a button that silently does nothing on someone's phone.
 */
export function PushToggle() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; tone: 'info' | 'warn' | 'ok' } | null>(null);
  const [needsKeys, setNeedsKeys] = useState(false);
  const [canGenerate, setCanGenerate] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      return;
    }
    setPermission(Notification.permission);
    // Reflect reality rather than assuming: a subscription can outlive a login, or be revoked in
    // browser settings without us hearing about it.
    navigator.serviceWorker.getRegistration('/console/').then(async (registration) => {
      const existing = await registration?.pushManager.getSubscription();
      setSubscribed(Boolean(existing));
    });
    // Ask what this deployment can actually do BEFORE offering an action. Without this the button
    // guessed, and the reader had to click the wrong thing and fail to be told the right one —
    // which reads as an error rather than as a step.
    api
      .pushKey()
      .then(({ publicKey, canGenerate: mayGenerate }) => {
        setNeedsKeys(!publicKey);
        setCanGenerate(mayGenerate);
      })
      .catch(() => {
        // Leave the button offering `enable`; that path reports its own failure clearly.
      });
  }, []);

  async function enable() {
    setBusy(true);
    setNote(null);
    try {
      const { publicKey, canGenerate: mayGenerate } = await api.pushKey();
      if (!publicKey) {
        // Distinguish "nobody has generated one" from "you cannot" — different problems, different
        // fixes, and the first one is a button rather than a support conversation.
        setNeedsKeys(true);
        setCanGenerate(mayGenerate);
        setNote({
          text: mayGenerate
            ? 'This deployment has no push key yet. Generate one first.'
            : 'Push is not set up on this deployment yet. An operator needs to generate a key.',
          // A step, not a fault — styling it as a warning made a normal first-run read as an error.
          tone: 'info',
        });
        return;
      }

      const registration = await navigator.serviceWorker.register('/console/sw.js', {
        scope: '/console/',
      });
      await navigator.serviceWorker.ready;

      const granted = await Notification.requestPermission();
      setPermission(granted);
      if (granted !== 'granted') {
        setNote({
          text:
            granted === 'denied'
              ? 'Notifications are blocked for this site. Browsers do not ask twice — you will need to allow them in site settings.'
              : 'Permission was dismissed.',
          tone: 'warn',
        });
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        // Required to be true by every browser: a push must always result in a visible
        // notification. Silent background pushes are not permitted.
        userVisibleOnly: true,
        applicationServerKey: publicKey,
      });

      const result = await api.pushSubscribe(subscription.toJSON(), describeThisBrowser());
      setSubscribed(true);
      setNote({
        text:
          result.scope === 'platform'
            ? 'This device will receive platform and fleet alerts.'
            : 'This device will receive alerts for your sites.',
        tone: 'ok',
      });
    } catch (e) {
      setNote({
        text: e instanceof Error ? e.message : 'Could not enable notifications.',
        tone: 'warn',
      });
    } finally {
      setBusy(false);
    }
  }

  async function generateKeys() {
    setBusy(true);
    setNote(null);
    try {
      await api.generatePushKeys();
      setNeedsKeys(false);
      setNote({
        text: 'Push key generated. Now enable notifications on this device.',
        tone: 'ok',
      });
    } catch (e) {
      setNote({
        text: e instanceof Error ? e.message : 'Could not generate a push key.',
        tone: 'warn',
      });
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    setNote(null);
    try {
      const registration = await navigator.serviceWorker.getRegistration('/console/');
      const existing = await registration?.pushManager.getSubscription();
      if (existing) {
        await api.pushUnsubscribe(existing.endpoint);
        await existing.unsubscribe();
      }
      setSubscribed(false);
      setNote({ text: 'This device will no longer receive alerts.', tone: 'ok' });
    } catch (e) {
      setNote({
        text: e instanceof Error ? e.message : 'Could not turn notifications off.',
        tone: 'warn',
      });
    } finally {
      setBusy(false);
    }
  }

  if (!supported) {
    return (
      <section>
        <h3>Browser notifications</h3>
        <p className="muted">
          This browser does not support push notifications. On iPhone or iPad, add this console to
          your home screen first — Safari only allows notifications for installed apps.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3>Browser notifications</h3>
      <p className="muted">Get critical alerts on this device, even when the console is closed.</p>
      {permission === 'denied' && !subscribed ? (
        <p className="warnbar">
          Notifications are blocked for this site. Browsers will not ask again — allow them in your
          site settings, then reload.
        </p>
      ) : (
        <button
          type="button"
          onClick={needsKeys && canGenerate ? generateKeys : subscribed ? disable : enable}
          disabled={busy || (needsKeys && !canGenerate)}
        >
          {busy
            ? 'Working…'
            : needsKeys && canGenerate
              ? 'Generate push key'
              : subscribed
                ? 'Turn off on this device'
                : 'Enable on this device'}
        </button>
      )}
      {note && (
        <p className={note.tone === 'ok' ? 'okbar' : note.tone === 'warn' ? 'warnbar' : 'muted'}>
          {note.text}
        </p>
      )}
    </section>
  );
}

/** A label the reader will recognise in a list of devices. Never derived from the endpoint. */
function describeThisBrowser(): string {
  const ua = navigator.userAgent;
  const browser = /Firefox/.test(ua)
    ? 'Firefox'
    : /Edg\//.test(ua)
      ? 'Edge'
      : /Chrome/.test(ua)
        ? 'Chrome'
        : /Safari/.test(ua)
          ? 'Safari'
          : 'Browser';
  const platform = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Mac/.test(ua)
        ? 'macOS'
        : /Win/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return platform ? `${browser} on ${platform}` : browser;
}
